/**
 * Drawing preferences that follow the user (the `drawing_prefs` field of the synced
 * `settings/main` document): magnet, keep drawing, lock all / hide all, favourite tools, the
 * per-tool default style ("Save as default") and named style templates.
 */
import { cloneStyle, sanitizeStyle } from "./model.ts";
import { isDrawingType, factoryStyle } from "./tools.ts";
import type { DrawingStyle, DrawingType, MagnetMode } from "./types.ts";

export type DrawingTemplate = { name: string; type: DrawingType; style: DrawingStyle };

export type DrawingPrefs = {
  magnet: MagnetMode;
  keepDrawing: boolean;
  lockAll: boolean;
  hideAll: boolean;
  favorites: DrawingType[];
  /** Per-tool style for new drawings ("Save as default"); absent = factory. */
  defaults: Partial<Record<DrawingType, DrawingStyle>>;
  templates: DrawingTemplate[];
};

export const DEFAULT_DRAWING_PREFS: DrawingPrefs = {
  magnet: "off",
  keepDrawing: false,
  lockAll: false,
  hideAll: false,
  favorites: ["trend_line", "horizontal_line", "fib_retracement", "rectangle", "long_position"],
  defaults: {},
  templates: [],
};

/** Most named templates kept. */
export const MAX_DRAWING_TEMPLATES = 100;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/** A stored style merged over the tool's factory style (invalid fields dropped). */
export function styleFor(type: DrawingType, raw: unknown): DrawingStyle {
  return sanitizeStyle(type, raw);
}

export function sanitizeDrawingPrefs(raw: unknown): DrawingPrefs {
  const d = DEFAULT_DRAWING_PREFS;
  if (!isRecord(raw)) return { ...d, favorites: [...d.favorites], defaults: {}, templates: [] };
  const magnet = raw["magnet"] === "weak" || raw["magnet"] === "strong" ? raw["magnet"] : "off";
  const favorites = Array.isArray(raw["favorites"])
    ? [...new Set(raw["favorites"].filter(isDrawingType))].slice(0, 30)
    : [...d.favorites];
  const defaults: Partial<Record<DrawingType, DrawingStyle>> = {};
  if (isRecord(raw["defaults"])) {
    for (const [k, v] of Object.entries(raw["defaults"])) if (isDrawingType(k)) defaults[k] = styleFor(k, v);
  }
  const templates: DrawingTemplate[] = [];
  if (Array.isArray(raw["templates"])) {
    for (const t of raw["templates"].slice(0, MAX_DRAWING_TEMPLATES)) {
      if (!isRecord(t) || !isDrawingType(t["type"]) || typeof t["name"] !== "string" || !t["name"].trim())
        continue;
      templates.push({
        name: t["name"].slice(0, 60),
        type: t["type"],
        style: styleFor(t["type"], t["style"]),
      });
    }
  }
  return {
    magnet,
    keepDrawing: raw["keepDrawing"] === true,
    lockAll: raw["lockAll"] === true,
    hideAll: raw["hideAll"] === true,
    favorites,
    defaults,
    templates,
  };
}

/** Style for a new drawing of `type`: the saved default, else the factory style. */
export function newDrawingStyle(prefs: DrawingPrefs, type: DrawingType): DrawingStyle {
  const s = prefs.defaults[type];
  return s ? cloneStyle(s) : factoryStyle(type);
}

export function toggleFavoriteTool(list: readonly DrawingType[], t: DrawingType): DrawingType[] {
  return list.includes(t) ? list.filter((x) => x !== t) : [...list, t];
}

/** Add or replace a named template (same name + tool replaces). */
export function upsertDrawingTemplate(
  list: readonly DrawingTemplate[],
  t: DrawingTemplate,
): DrawingTemplate[] {
  const rest = list.filter((x) => !(x.name === t.name && x.type === t.type));
  return [...rest, { ...t, style: cloneStyle(t.style) }].slice(-MAX_DRAWING_TEMPLATES);
}
